import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import useImportacionesProduccion from "../../../hooks/administrador/adheridos/useImportacionesProduccion";
import "../../../styles/administrador.css";

const ImportacionesProduccion = ({ year, mes, token, idAdministrador }) => {
  const chartRef = useRef(null);
  const { data, loading, error } = useImportacionesProduccion(
    year,
    mes,
    token,
    idAdministrador
  );

  useEffect(() => {
    if (loading || !chartRef.current) return;

    if (error || !Array.isArray(data) || data.length === 0) {
      if (chartRef.current) {
        chartRef.current.innerHTML =
          "<p style='text-align: center; color: black;'>No hay datos disponibles</p>";
      }
      return;
    }

    const chartDom = chartRef.current;
    let myChart = echarts.getInstanceByDom(chartDom);
    if (myChart) myChart.dispose();
    myChart = echarts.init(chartDom);

    const colorPalette = [
      "#d1dc5a",
      "#6ac5cb",
      "#45444e",
      "#a3b85b",
      "#3a6b74",
      "#7d8a2e",
      "#3d9199",
      "#62615e",
      "#879b4a",
      "#2b5258",
    ];

     
    data.sort((a, b) => parseFloat(b.cantidad) - parseFloat(a.cantidad));
    const top10Data = data.slice(0, 10);
    const otrosData = data.slice(10);

    const otrosTotal = otrosData.reduce(
      (sum, item) => sum + parseFloat(item.cantidad || 0),
      0
    );

    const formattedData = top10Data.map((item, index) => ({
      name: item.razon_social,
      value: parseFloat(item.cantidad || 0),
      itemStyle: {
        color: colorPalette[index % colorPalette.length],
      },
    }));

    if (otrosTotal > 0) {
      formattedData.push({
        name: "OTROS",
        value: otrosTotal,
        itemStyle: {
          color: colorPalette[formattedData.length % colorPalette.length],
        },
      });
    }

    const totalImportaciones = formattedData.reduce(
      (sum, item) => sum + item.value,
      0
    );

    const option = {
      title: {
        text: "Importación y producción acumulado",
        subtext: `TOTAL DE IMPORTACIONES: ${totalImportaciones.toLocaleString()}`,
        left: "center",
        top: "10",
        textStyle: {
          fontSize: 18,
          fontWeight: "bold",
        },
        subtextStyle: {
          fontSize: 12,
          fontWeight: "bold",
        },
      },
      tooltip: {
        enabled: false,
        show: false,
        trigger: "item",
        formatter: function (params) {
          return `${
            params.name
          }: ${params.value.toLocaleString()} (${params.percent.toFixed(1)}%)`;
        },
      },
      graphic: {
        type: "group",
        left: "center",
        top: "center",
        children: [
          {
            type: "text",
            id: "centerText",
            left: "center",
            top: "center",
            style: {
              text: `Total\n${totalImportaciones.toLocaleString()}`,
              textAlign: "center",
              fontSize: 16,
              fontWeight: "bold",
              lineHeight: 20,
            },
          },
        ],
      },
        legend: {
    show: true,
    type: "scroll",  
    orient: "horizontal",  
    left: "center",  
    bottom: "10",  
    textStyle: {
      fontSize: 12,
      color: "#333",
    },
    formatter: function (name) {
      const item = formattedData.find((item) => item.name === name);
      const cantidad = item ? item.value.toLocaleString("es-ES") : 0;
      const shortenedName =
        name.length > 13 ? `${name.slice(0, 13)}...` : name;  
      return `${shortenedName}\n${cantidad} [U]`;  
    },
    pageIconColor: "#333",  
    pageIconSize: 23,  
    pageTextStyle: {
      fontSize: 12,
      color: "#333",
    },
  },
      series: [
        {
          name: "Importaciones",
          type: "pie",
          radius: ["40%", "70%"],  
          center: ["50%", "50%"],
          label: {
            show: false,
          },
          data: formattedData.map((item, index) => ({
          ...item,
          itemStyle: { 
              color: colorPalette[index % colorPalette.length],
              borderColor: "#ffffff",  
              borderWidth: 4,  
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        })),
          labelLine: {
            show: false,
          },
        },
      ],
    };

    myChart.setOption(option);

    myChart.on("click", function (params) {
      if (params.componentType === "series") {
        myChart.setOption({
          graphic: {
            children: [
              {
                type: "text",
                id: "centerText",
                left: "center",
                top: "center",
                style: {
                  text: `${params.name.replace(
                    /(.{15})/g,
                    "$1\n"
                  )}\n${params.value.toLocaleString()} [U]`,  
                  textAlign: "center",
                  fontSize: 14,  
                  fontWeight: "bold",  
                  lineHeight: 18,  
                },
              },
            ],
          },
        });
      }
    });

     
    myChart.on("mouseout", function () {
      myChart.setOption({
        graphic: {
          children: [
            {
              type: "text",
              id: "centerText",
              left: "center",
              top: "center",
              style: {
                text: `Total\n${totalImportaciones.toLocaleString()}`,
                textAlign: "center",
                fontSize: 16,
                fontWeight: "bold",
                lineHeight: 20,
              },
            },
          ],
        },
      });
    });

    const updateChart = () => {
      myChart.resize();
    };

    window.addEventListener("resize", updateChart);

    return () => {
      window.removeEventListener("resize", updateChart);
      if (myChart && !myChart.isDisposed()) {
        myChart.dispose();
      }
    };
  }, [data, loading, error]);

  if (loading) {
    return (
      <div className="grafica loading">
        Cargando datos de importaciones y producción...
      </div>
    );
  }

  return <div className="grafica" ref={chartRef}></div>;
};

export default ImportacionesProduccion;
